import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { sanitizeRedirectUrl } from '@common/utils/sanitizeRedirectUrl';
import { useThemeQuery } from '../../reactQuery/get';
import CompletionButton from './CompletionButton';

// TELV1-551 — this component innerHTML's backend-templated message text,
// and the markdown rewrite below builds raw HTML from message content.
// Everything interpolated into that HTML string must be entity-escaped.
const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

interface MessageContentParserProps {
  content: string;
  className?: string;
}

function MessageContentParser({ content, className }: MessageContentParserProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { organization_id, project_id, chat_id } = useParams();
  const projectId = organization_id ? project_id : chat_id;
  const { data: themeData } = useThemeQuery(projectId);

  // Function to convert plain URLs to clickable links
  const linkifyUrls = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      // Regex to match URLs
      const urlRegex = /(?:https?:\/\/|www\.)[^\s<]+(?:\.[^\s<]+)+(?:\/[^\s<]*)?/gi;

      if (urlRegex.test(text)) {
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        urlRegex.lastIndex = 0; // Reset regex
        // eslint-disable-next-line no-cond-assign
        while ((match = urlRegex.exec(text)) !== null) {
          // Add text before the URL
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }

          // Create link element
          const link = document.createElement('a');
          // eslint-disable-next-line prefer-destructuring
          let href = match[0];
          // Add protocol if missing
          if (!href.startsWith('http://') && !href.startsWith('https://')) {
            href = `https://${href}`;
          }
          link.href = href;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          // eslint-disable-next-line prefer-destructuring
          link.textContent = match[0];
          link.className = 'text-blue-500 underline hover:text-blue-700';
          fragment.appendChild(link);

          lastIndex = match.index + match[0].length;
        }

        // Add remaining text
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }

        // Replace the text node with the fragment
        if (node.parentNode) {
          node.parentNode.replaceChild(fragment, node);
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Skip certain elements
      const element = node as HTMLElement;
      if (element.tagName !== 'A' && element.tagName !== 'CODE' && element.tagName !== 'PRE') {
        // Process child nodes - iterate backwards to avoid issues with DOM mutations
        const childNodes = Array.from(node.childNodes);
        // Process nodes in reverse order to handle DOM mutations safely
        // eslint-disable-next-line no-plusplus
        for (let i = childNodes.length - 1; i >= 0; i--) {
          linkifyUrls(childNodes[i]);
        }
      }
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // First, convert markdown links to HTML.
    // TELV1-551 — both captures come from message content and used to be
    // interpolated RAW into an innerHTML string (attribute breakout +
    // script-scheme links). Escape both; drop the anchor entirely for
    // script schemes and keep the visible text.
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const contentWithHtmlLinks = content.replace(markdownLinkRegex, (match, text, url) => {
      const safeUrl = sanitizeRedirectUrl(String(url).trim());
      if (!safeUrl) return escapeHtml(text);
      return `<a href="${escapeHtml(safeUrl)}" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline hover:text-blue-700">${escapeHtml(text)}</a>`;
    });

    // Set the HTML content with converted links
    containerRef.current.innerHTML = contentWithHtmlLinks;

    // Convert remaining plain URLs to links
    linkifyUrls(containerRef.current);

    // Check if this message contains a completion link using data attributes
    const completionLink = containerRef.current.querySelector('[data-completion-link="true"]');

    if (completionLink) {
      // TELV1-551 — the completion href is the ONE project-configured
      // string that reaches window.location with auto-navigation; a
      // `javascript:` link executes in global scope (Sentry
      // TELLET-CHAT-G4). Blocked scheme -> neutralize the live anchor
      // (it was already written into the DOM by innerHTML above) and
      // fall through WITHOUT mounting the button: the respondent keeps
      // the textual fallback + <code> line, nothing is clickable.
      const href = sanitizeRedirectUrl(completionLink.getAttribute('href'));
      if (!href) {
        completionLink.removeAttribute('href');
      }
      // Since both URLs are the same, we use href for both
      const buttonText = completionLink.textContent || 'Complete Interview';
      const autoRedirect = completionLink.getAttribute('data-auto-redirect') === 'true';

      /**
       * Extract localized strings from data attributes
       * These are embedded by the backend based on user's language preference
       */
      const redirectingIn = completionLink.getAttribute('data-redirecting-in') || 'Redirecting in';
      const seconds = completionLink.getAttribute('data-seconds') || 'seconds...';
      const cancel = completionLink.getAttribute('data-cancel') || 'Cancel';

      if (href) {
        // Extract the fallback text (text after the link but before the code element)
        let fallbackText = "Can't click the button? Copy this link:";
        let currentNode = completionLink.nextSibling;
        while (currentNode) {
          if (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim()) {
            fallbackText = currentNode.textContent.trim();
            break;
          }
          if (
            currentNode.nodeType === Node.ELEMENT_NODE &&
            (currentNode as HTMLElement).tagName === 'CODE'
          ) {
            break;
          }
          currentNode = currentNode.nextSibling;
        }

        // Find the parent of the completion link to replace the whole section
        const linkParent = completionLink.parentElement;

        // Create a container for the button component
        const buttonContainer = document.createElement('div');

        // Replace the link and its surrounding text with the button container
        if (linkParent) {
          // Remove the completion link section (link + br + fallback text + br + code)
          const nodesToRemove = [];
          // eslint-disable-next-line @typescript-eslint/no-shadow
          let currentNode = completionLink.previousSibling;
          // Remove text before link (completion message)
          while (currentNode && currentNode.nodeType === Node.TEXT_NODE) {
            nodesToRemove.push(currentNode);
            currentNode = currentNode.previousSibling;
          }
          // Remove link and everything after it including the code element
          currentNode = completionLink;
          while (currentNode) {
            nodesToRemove.push(currentNode);
            currentNode = currentNode.nextSibling;
            // Stop after we've included the code element
            if (
              nodesToRemove[nodesToRemove.length - 1].nodeType === Node.ELEMENT_NODE &&
              (nodesToRemove[nodesToRemove.length - 1] as HTMLElement).tagName === 'CODE'
            ) {
              break;
            }
          }

          // Remove all collected nodes
          nodesToRemove.forEach((node) => node.remove());

          // Insert button container
          linkParent.appendChild(buttonContainer);
        }

        // Use React Portal to render the button component
        import('react-dom/client').then(({ createRoot }) => {
          const root = createRoot(buttonContainer);
          root.render(
            <CompletionButton
              url={href}
              text={buttonText}
              fallbackText={fallbackText}
              autoRedirect={autoRedirect}
              primaryColor={themeData?.primary}
              chatPrimary={themeData?.['chat-primary']}
              chatSecondary={themeData?.['chat-secondary']}
              translations={{
                redirectingIn,
                seconds,
                cancel,
                copied: 'Copied!',
                cantClickButton: fallbackText,
              }}
            />,
          );
        });
      }
    }
  }, [content, themeData]);

  return <div ref={containerRef} className={className} dir="auto" />;
}

export default MessageContentParser;
